// Swagger: https://api-dev.splashsports.com/commissioner-service/api/docs
import queryString from 'query-string';

import type {
  Channel,
  ChannelOnboardingPayload,
  CommissionerAssetType,
  GetChannelResponse,
} from '~/domains/commissioner/interfaces/channel.interface';
import type {
  GetContestChatResponse,
  GetLiveLikeProfileResponse,
} from '~/domains/commissioner/interfaces/chat.interface';
import type { GetCommissionerFollowersResponse } from '~/domains/commissioner/interfaces/follower.interface';
import type { Group, Member } from '~/domains/commissioner/interfaces/groups.interface';
import type { GetPostCommentResponse } from '~/domains/commissioner/interfaces/post-comment.interface';
import type {
  GetCommissionerPostsResponse,
  Post,
} from '~/domains/commissioner/interfaces/post.interface';
import type {
  GetCommissionerRewardsRequirementResponse,
  GetCommissionerRewardsRulesResponse,
  GetRecentRewardsResponse,
} from '~/domains/commissioner/interfaces/rewards.interface';
import { convertPaginationToSwagFormat } from '~/domains/common/utils/convertPaginationToSwagFormat';
import { createQueryString } from '~/domains/common/utils/queryString';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type { ContestInvite } from '~/domains/contest/domains/common/components/ContestInviteCard/types';

import {
  commissionerImageUploadService,
  commissionerService,
} from './commissioner-service.service.instance';
import { parseContestMessageAllowance } from './contest-message-allowance.schema';

export const postCommissionerChannelData = async (payload: ChannelOnboardingPayload) => {
  const response = await commissionerService.post<GetChannelResponse>('/channels', payload);
  return response.data;
};

export const patchCommissionerChannelData = async (
  payload: ChannelOnboardingPayload,
  id: string
) => {
  const response = await commissionerService.patch<GetChannelResponse>(`/channels/${id}`, payload);
  return response.data;
};

/** @deprecated use getChannelsByQuery instead */
export const getCommissionersChannelsByQuery = async ({
  query,
  limit,
  offset = 0,
}: {
  query: string;
  limit?: number;
  offset?: number;
}) => {
  const { pageSize, page } = convertPaginationToSwagFormat({ limit, offset });
  const response = await commissionerService.get<PaginatedData<Channel>>(
    `/channels${createQueryString({ q: query, pageSize, page })}`
  );
  return response.data;
};

type InfiniteData<T> = {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  id: string;
  data: T[];
};

export type OrganizerChannel = Pick<Channel, 'id' | 'userId' | 'handle' | 'name' | 'avatarUrl'>;

export const getChannelsByQuery = async (
  payload: {
    q: string;
    page?: number;
    pageSize?: number;
  },
  signal?: AbortSignal
) => {
  const query = queryString.stringify(payload);
  const response = await commissionerService.get<InfiniteData<OrganizerChannel>>(
    `/channels?${query}`,
    { signal }
  );

  return response.data;
};

export const getCommissionerChannelDataBySlug = async (slug: string) => {
  const response = await commissionerService.get<GetChannelResponse>(`/channels/slug/${slug}`);
  return response.data;
};

export const getCommissionerChannelDataByUserId = async (
  userId: string,
  signal?: AbortSignal,
  { withStats = true }: { withStats?: boolean } = {}
) => {
  const response = await commissionerService.get<GetChannelResponse>(
    `/channels/user/${userId}?withStats=${withStats}`,
    { signal }
  );
  return response.data;
};

export const getCommissionerChannelDataByHandle = async (handle: string) => {
  const response = await commissionerService.get<GetChannelResponse>(`/channels/handle/${handle}`);
  return response.data;
};

export const getPopularChannels = async () => {
  const response = await commissionerService.get<InfiniteData<Channel>>('/channels/popular');
  return response.data;
};

export const getCommissionerChannelHandleAvailability = async (handle: string) => {
  const response = await commissionerService.get<{
    data: {
      isAvailable: boolean;
    };
  }>(`/channels/handles/${handle}/available`);
  return response.data;
};

export const getCommissionerChannelDataByUser = async (signal?: AbortSignal) => {
  const response = await commissionerService.get<GetChannelResponse>('/channels/user', { signal });
  return response.data;
};

export const uploadCommissionerAsset = async (payload: FormData, path: CommissionerAssetType) => {
  const response = await commissionerImageUploadService.post(`/${path}`, payload);
  return response.data;
};

/** @deprecated */
export const followCommissioner = async (channelId: string, username: string) => {
  const response = await commissionerService.post(`/channels/${channelId}/followers`, { username });
  return response.data;
};

export const followChannel = async (channelId: string) => {
  const response = await commissionerService.post(`/channels/${channelId}/followers`);
  return response.data;
};

export const unfollowChannel = async (channelId: string) => {
  const response = await commissionerService.delete(`/channels/${channelId}/followers`);
  return response.data;
};

type InfiniteDataPayload = {
  page?: number;
  pageSize?: number;
  contestId?: string;
  contestInviteStatuses?: ('ACCEPTED' | 'PENDING')[];
  q?: string;
};

export const getChannelFollowers = async (channelId: string, payload: InfiniteDataPayload) => {
  const query = queryString.stringify(payload);
  const response = await commissionerService.get<GetCommissionerFollowersResponse>(
    `/channels/${channelId}/followers?${query}`
  );
  return response.data;
};

type GetChannelFollowersPagesPayload = {
  channelId: string;
  limit?: number;
  offset?: number;
  q?: string;
};

export const getChannelFollowersPages = async ({
  channelId,
  limit = 50,
  offset = 0,
  q,
}: GetChannelFollowersPagesPayload) => {
  const { pageSize, page } = convertPaginationToSwagFormat({ limit, offset });

  const queryParams = {
    pageSize,
    page,
    ...(q ? { q } : {}),
  };

  const response = await commissionerService.get<GetCommissionerFollowersResponse>(
    `/channels/${channelId}/followers${createQueryString(queryParams)}`
  );
  return response.data;
};

export const getChannelFollowees = async (channelId: string, payload: InfiniteDataPayload) => {
  const query = queryString.stringify(payload);
  const response = await commissionerService.get<GetCommissionerFollowersResponse>(
    `/channels/${channelId}/followees?${query}`
  );
  return response.data;
};

export const getCommissionerPosts = async (channelId: string, payload: InfiniteDataPayload) => {
  const query = queryString.stringify(payload);
  const response = await commissionerService.get<GetCommissionerPostsResponse>(
    `/channels/${channelId}/posts?${query}`
  );
  return response.data;
};

export const createNewPost = async (channelId: string, post: Post) => {
  const response = await commissionerService.post(`/channels/${channelId}/posts`, post);
  return response.data;
};

export const likePost = async (postId: string) => {
  const response = await commissionerService.post(`/posts/${postId}/likes`);
  return response.data;
};

export const unlikePost = async (postId: string) => {
  const response = await commissionerService.delete(`/posts/${postId}/likes`);
  return response.data;
};

export const getComments = async (postId: string) => {
  const response = await commissionerService.get<GetPostCommentResponse>(
    `/posts/${postId}/comments`
  );
  return response.data;
};

export const addComment = async (postId: string, text: string) => {
  const response = await commissionerService.post(`/posts/${postId}/comments`, {
    text,
  });
  return response.data;
};

/** @deprecated - Used for old commish hub */
export const getCommissionerTotalHandle = async (channelId: string) => {
  const response = await commissionerService.get(`/channels/${channelId}/handles/total`);
  return response.data;
};

/** @deprecated - Used for old commish hub */
export const getCommissionerTotalRewards = async (channelId: string) => {
  const response = await commissionerService.get(`/channels/${channelId}/rewards/total`);
  return response.data;
};

/** @deprecated - Used for old commish hub */
export const getCommissionerRecentRewards = async (channelId: string) => {
  const response = await commissionerService.get<GetRecentRewardsResponse>(
    `/channels/${channelId}/rewards`
  );
  return response.data;
};

/** @deprecated - Used for old commish hub */
export const getCommissionerRewardsRules = async () => {
  const response =
    await commissionerService.get<GetCommissionerRewardsRulesResponse>('/rewards/rules');
  return response.data?.data;
};

/** @deprecated - Used for old commish hub */
export const getCommissionerRewardsRequirements = async () => {
  const response =
    await commissionerService.get<GetCommissionerRewardsRequirementResponse>(
      '/rewards/requirements'
    );
  return response.data?.data;
};

export const getLiveLikeProfile = async () => {
  const response = await commissionerService.get<GetLiveLikeProfileResponse>('/livelike/profiles');

  return response.data?.data;
};

export const getContestChat = async (contestId: string) => {
  const response = await commissionerService.get<GetContestChatResponse>(
    `/livelike/chats/contests/${contestId}`
  );

  return response.data?.data;
};

// eslint-disable-next-line no-restricted-syntax
enum OverviewTimeframe {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}
/** @deprecated - Used for old commish hub */
export const getCommissionerConsoleOverview = async (timeframe: OverviewTimeframe) => {
  const response = await commissionerService.get(`/channels/user/overview?timeframe=${timeframe}`);
  return response.data;
};

export const createGroup = async (name: string) => {
  const response = await commissionerService.post<{ id: string; data: Group }>(`/groups`, {
    name,
  });
  return response.data?.data;
};

export const getGroups = async ({
  limit = 20,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
}) => {
  const { pageSize, page } = convertPaginationToSwagFormat({ limit, offset });

  const response = await commissionerService.get<PaginatedData<Group>>(
    `/groups${createQueryString({ pageSize, page })}`
  );
  return response.data;
};

export const updateGroup = async (id: string, name: string) => {
  const response = await commissionerService.post<{ id: string; data: Group }>(`/groups/${id}`, {
    name,
  });
  return response.data?.data;
};

export const deleteGroup = async (id: string) => {
  await commissionerService.delete(`/groups/${id}`);
};

export const addMembers = async (id: string, userIds: string[]) => {
  await commissionerService.post(`/groups/${id}/members`, {
    userIds,
  });
};

export const getMembers = async (
  id: string,
  {
    limit = 20,
    offset = 0,
  }: {
    limit?: number;
    offset?: number;
  }
) => {
  const { pageSize, page } = convertPaginationToSwagFormat({ limit, offset });

  const response = await commissionerService.get<PaginatedData<Member>>(
    `/groups/${id}/members${createQueryString({ pageSize, page })}`
  );
  return response.data;
};

export const removeMembers = async (id: string, userIds: string[]) => {
  await commissionerService.delete(`/groups/${id}/members`, { data: { userIds } });
};

export const inviteChannelToContests = async (channelId: string, contestIds: string[]) => {
  const response = await commissionerService.post(`/channels/${channelId}/invites`, {
    contestIds,
  });
  return response.data;
};

export type ContestInvitePayload = {
  allContests?: boolean;
  allUsers?: boolean;
  contestIds?: string[];
  v2ContestIds?: string[];
  nativeInvites?: boolean;
  emails?: string[];
  error?: string;
  excludedContestIds?: string[];
  excludedGroupIds?: string[];
  excludedUserIds?: string[];
  groupIds?: string[];
  rypPoolIds?: string[];
  rypUserIds?: string[];
  ofpContestIds?: string[];
  ofpUserIds?: string[];
  excludedContestUserIds?: string[];
  contestUserIds?: string[];
  excludedOfpUserIds?: string[];
  excludedRypUserIds?: string[];
  userIds?: string[];
  customMessage?: string;
  from?: string;
};

type CreateContestResponse = {
  id: string;
  data: {
    count: number;
  };
};

export const createContestInvite = async (contestId: string, payload: ContestInvitePayload) => {
  const response = await commissionerService.post<CreateContestResponse>(
    `/contests/${contestId}/invites`,
    payload,
    {
      headers: {
        'splash-accept-version': '2',
      },
    }
  );
  return response.data;
};

type ChannelInvitesOverviewResponse = {
  total: number;
  contests: {
    id: string;
    name: string;
    total: number;
  }[];
};

export const getChannelInvitesOverview = async (channelId: string) => {
  const response = await commissionerService.get<ChannelInvitesOverviewResponse>(
    `/channels/${channelId}/invites/overview`
  );
  return response.data;
};

export type ContestInvitesStats = {
  invited: number;
  pending: number;
  accepted: number;
  followers: number;
  previousSplashContests: number;
  ofpPools: number;
  ofpUsers: number;
  rypPools: number;
  rypUsers: number;
  splashContests: number;
};

export type ContestInvitesStatsResponse = {
  id: string;
  data: {
    description: string;
    stats: ContestInvitesStats;
    emailInvites: number;
    invitesProcessing: boolean;
  };
};

export const getContestInvitesStats = async (contestId: string, channelId: string) => {
  const response = await commissionerService.get<ContestInvitesStatsResponse>(
    `/contests/${contestId}/invites/stats?channelId=${channelId}`,
    {
      headers: {
        'splash-accept-version': '2',
      },
    }
  );
  return response.data.data;
};

type ContestInviteDto = {
  id: string;
  userId: string;
  channelId: string;
  channelName: string;
  handle?: string;
  email?: string;
  status: 'ACCEPTED' | 'PENDING';
  createdAt: Date;
};

export type UpdateContestInviteStatusPayload = {
  status: 'ACCEPTED' | 'PENDING' | 'DECLINED';
};

export type UpdateContestInviteStatusDto = {
  id: string;
  userId: string;
  channelId: string;
  channelName: string;
  handle?: string;
  email?: string;
  avatarUrl?: string;
  status: UpdateContestInviteStatusPayload['status'];
  source?: string;
  createdAt: Date;
};

export type UpdateContestInviteStatusResponse = {
  id: string;
  data: UpdateContestInviteStatusDto;
};

export const updateContestInviteStatus = async (
  contestInviteId: string,
  payload: UpdateContestInviteStatusPayload
) => {
  const response = await commissionerService.patch<UpdateContestInviteStatusResponse>(
    `/contest-invites/${contestInviteId}`,
    payload
  );
  return response.data;
};

export const getContestInvites = async (
  channelId: string,
  contestId: string,
  payload: InfiniteDataPayload
) => {
  const query = queryString.stringify(payload);

  const response = await commissionerService.get<InfiniteData<ContestInviteDto>>(
    `/channels/${channelId}/contests/${contestId}/invites?${query}`,
    {
      headers: {
        'splash-accept-version': '2',
      },
    }
  );
  return response.data;
};

// ------------------------------ CONTEST INVITES DOWNLOAD ------------------------------
//  Initiates a csv file generation for the contest invites
type QueueContestInvitesDownloadResponse = {
  id: string;
  data: {
    id: string;
    status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  };
};
export const queueContestInvitesDownload = async (channelId: string, contestId: string) => {
  const response = await commissionerService.post<QueueContestInvitesDownloadResponse>(
    `/csv-invites`,
    {
      channelId,
      contestId,
    }
  );
  return response.data;
};

// Polls the status of the contest invites download job
export const pollContestInvitesDownloadStatus = async (jobId: string) => {
  const response = await commissionerService.get<QueueContestInvitesDownloadResponse>(
    `/csv-invites/${jobId}`
  );
  return response.data;
};

// Downloads the csv file for the contest invites when the job is completed
export const downloadContestInvites = async (jobId: string) => {
  const response = await commissionerService.get<Blob>(`/csv-invites/${jobId}/file`, {
    responseType: 'blob',
    headers: {
      Accept: 'text/csv',
      'Content-Type': 'text/csv',
    },
  });
  return response.data;
};

// Download the members of an EO pool in CSV format
// Note: Do NOT set `Content-type` header, it won't work on the backend for whatever reason
export const downloadPoolMembers = async (
  contestId: string,
  rypPoolIds: string[],
  ofpPoolIds: string[]
) => {
  const response = await commissionerService.post<Blob>(
    `/contests/${contestId}/invites/participants`,
    {
      rypPoolIds,
      ofpPoolIds,
    },
    {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    }
  );
  return response.data;
};

export type ContestReinvitePayload = {
  contestId: string;
};

export const resendPendingInvites = async (contestId: string, authToken?: string) => {
  const response = await commissionerService.post(
    `/contests/${contestId}/invites/pending`,
    {
      contestId,
    },
    {
      headers: {
        'splash-accept-version': '2',
      },
    }
  );
  return response.data;
};

type InviteLink = {
  contestId: string;
  channelId: string;
  userId: string;
  source: string;
  medium: string;
  content: string;
};

export type SendCustomEmailPayload = {
  contestId: string;
  to: string;
  from: string;
  message: string;
  inviteLink?: InviteLink;
  userIds?: string[];
};

export const sendCustomEmail = async (payload: SendCustomEmailPayload) => {
  const response = await commissionerService.post(
    `/contests/${payload.contestId}/messages`,
    payload
  );
  return response;
};

export const getContestMessageAllowance = async (contestId: string, signal?: AbortSignal) => {
  const response = await commissionerService.get<unknown>(
    `/contests/${contestId}/messages/allowance`,
    { signal }
  );
  return parseContestMessageAllowance(response.data);
};

export type GetChannelInvitesReceivedPayload = {
  status?: Exclude<UpdateContestInviteStatusPayload['status'], 'DECLINED'>;
  limit?: number;
  offset?: number;
};

export type GetChannelInvitesReceivedResponse = {
  data: ContestInvite[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

type GetChannelInvitesReceivedApiResponse = {
  id: string;
  data: ContestInvite[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export const getChannelInvitesReceived = async (payload?: GetChannelInvitesReceivedPayload) => {
  const query = payload ? queryString.stringify(payload) : '';
  const url = `/channels/invites/received${query ? `?${query}` : ''}`;

  const response = await commissionerService.get<GetChannelInvitesReceivedApiResponse>(url);

  return response.data;
};
